import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { LoaderCircle } from 'lucide-react';
import type { ControlCenterFeature, ControlCenterFeatureFlags } from '../models';
import { getControlCenterFeatureFlags, getToken } from '../services/api';

interface FeatureFlagsContextValue {
  flags: ControlCenterFeatureFlags | null;
  loading: boolean;
  isEnabled: (feature: ControlCenterFeature) => boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: null,
  loading: false,
  isEnabled: () => false,
});

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const token = getToken();
  const loadedToken = useRef('');
  const [flags, setFlags] = useState<ControlCenterFeatureFlags | null>(null);
  const [loading, setLoading] = useState(Boolean(token));

  useEffect(() => {
    if (!token) {
      loadedToken.current = '';
      setFlags(null);
      setLoading(false);
      return;
    }
    if (loadedToken.current === token) return;
    let active = true;
    setLoading(true);
    getControlCenterFeatureFlags()
      .then((response) => {
        if (!active) return;
        setFlags(response.data.data);
        loadedToken.current = token;
      })
      .catch(() => {
        if (!active) return;
        setFlags(null);
        loadedToken.current = '';
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [location.pathname, token]);

  return <FeatureFlagsContext.Provider value={{
    flags,
    loading,
    isEnabled: (feature) => flags?.[feature] === true,
  }}>{children}</FeatureFlagsContext.Provider>;
}

export const useFeatureFlags = () => useContext(FeatureFlagsContext);

export function FeatureGate({ feature, children }: { feature: ControlCenterFeature; children: ReactNode }) {
  const { loading, isEnabled } = useFeatureFlags();
  if (loading) return <div className="flex min-h-screen items-center justify-center gap-3 bg-background text-sm text-muted-foreground" role="status">
    <LoaderCircle className="h-5 w-5 animate-spin" />Загрузка конфигурации разделов
  </div>;
  return isEnabled(feature) ? <>{children}</> : <Navigate to="/" replace />;
}
